import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { interval, startWith } from 'rxjs';
import { NotificationApiService } from '../../core/api-services/notification-api.service';
import { AuthenticationService } from '../../core/services/authentication.service';
import { AuthorizationService } from '../../core/authorization/authorization.service';
import { Permissions } from '../../core/authorization/permissions';
import { AppNotification } from '../../core/models/notification';

/**
 * Header notification tray.
 *
 * Students are notified about application and flight booking changes; reviewers
 * about new applications, new flight booking requests and so on. The unread
 * badge refreshes on a timer so the tray stays current without a page reload.
 */
@Component({
  selector: 'app-notifications-tray',
  standalone: true,
  imports: [CommonModule, DatePipe],
  templateUrl: './notifications-tray.html',
  styleUrl: './notifications-tray.scss',
})
export class NotificationsTrayComponent implements OnInit {
  private readonly api = inject(NotificationApiService);
  private readonly auth = inject(AuthenticationService);
  private readonly authorization = inject(AuthorizationService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** How often the unread badge is refreshed while the user is signed in. */
  private static readonly PollIntervalMs = 60_000;

  readonly notifications = signal<AppNotification[]>([]);
  readonly unreadCount = signal(0);
  readonly open = signal(false);
  readonly loading = signal(false);
  readonly error = signal('');

  ngOnInit(): void {
    if (!this.auth.isLoggedIn()) {
      return;
    }

    interval(NotificationsTrayComponent.PollIntervalMs)
      .pipe(startWith(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshUnreadCount());
  }

  toggle(): void {
    const next = !this.open();
    this.open.set(next);
    if (next) {
      this.load();
    }
  }

  close(): void {
    this.open.set(false);
  }

  load(): void {
    this.loading.set(true);
    this.error.set('');
    this.api
      .getNotifications(false, 20)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (notifications) => {
          this.notifications.set(notifications);
          this.unreadCount.set(notifications.filter((entry) => !entry.isRead).length);
          this.loading.set(false);
        },
        error: () => {
          this.error.set('Notifications could not be loaded.');
          this.loading.set(false);
        },
      });
  }

  markAllRead(): void {
    this.api
      .markAllRead()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.notifications.set(this.notifications().map((entry) => ({ ...entry, isRead: true })));
          this.unreadCount.set(0);
        },
        error: () => this.error.set('Notifications could not be updated.'),
      });
  }

  /** Marks the entry read, then takes the user to what it is about. */
  select(notification: AppNotification): void {
    if (!notification.isRead) {
      this.api
        .markRead(notification.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.notifications.set(
              this.notifications().map((entry) => (entry.id === notification.id ? { ...entry, isRead: true } : entry))
            );
            this.refreshUnreadCount();
          },
          // A failed "mark read" must not stop the user from reaching the page.
          error: () => undefined,
        });
    }

    this.close();
    const target = this.resolveActionUrl(notification);
    if (target) {
      this.router.navigateByUrl(target);
    }
  }

  /**
   * Where a notification should take the user.
   *
   * Events are broadcast to a mixed audience — administrators who manage events
   * and attendees who register for them both hold the "register for events"
   * permission — so the portal is chosen from what the signed-in user may do
   * rather than from the stored link. Without this an administrator clicking an
   * event notification would land in the student portal and its side menu.
   */
  private resolveActionUrl(notification: AppNotification): string | null {
    const url = notification.actionUrl;
    if (!url) {
      return null;
    }

    if (notification.category !== 'EVENT') {
      return url;
    }

    const section = url.includes('section=') ? url.slice(url.indexOf('section=')) : 'section=events';
    return this.authorization.hasPermission(Permissions.EventManage)
      ? `/admin?${section}`
      : `/student?${section}`;
  }

  iconClass(category: string): string {
    switch (category) {
      case 'APPLICATION':
        return 'fas fa-file-alt';
      case 'DOCUMENT':
        return 'fas fa-file-upload';
      case 'FLIGHT_REQUEST':
        return 'fas fa-plane';
      case 'CLAIM':
        return 'fas fa-receipt';
      case 'EVENT':
        return 'fas fa-calendar-alt';
      default:
        return 'fas fa-bell';
    }
  }

  private refreshUnreadCount(): void {
    if (!this.auth.isLoggedIn()) {
      this.unreadCount.set(0);
      return;
    }

    this.api
      .getUnreadCount()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (count) => this.unreadCount.set(count),
        error: () => undefined,
      });
  }
}
